import { useLocation } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { callSurfaceMode, isDesktopCallWindow } from '@/features/desktop/callWindow';
import { useStoreValue } from '@/lib/tinyStore';
import { liveCallStore, selectLiveCall } from '../store/liveCallStore';
import { CallSurface } from './CallSurface';

/**
 * The live call, mounted ABOVE the router so navigation cannot end it.
 *
 * This is the whole point of the arrangement. `/call/:callId` used to own the
 * media surface, which meant React unmounted the call the moment you clicked
 * Chat — leaving the app during a call hung up on the person you were talking
 * to. The separate pop-up window was an attempt to escape that, and it brought
 * its own problems (no token on boot, blocked by the browser, a state store the
 * workspace could not see).
 *
 * Keeping the surface here solves it without any of that. The route becomes a
 * marker rather than a host, and where you happen to be standing only decides
 * how BIG the call is:
 *
 *   • on `/call/:id` — full-bleed, the whole window.
 *   • anywhere else  — a tile in the corner, still connected, one click back.
 *
 * `CallSurface` is mounted in BOTH cases and is the same element either way, so
 * React keeps the media session alive across the change; only its wrapper is
 * swapped. Rendering two different trees, or moving it between parents, would
 * remount it and drop the call — which is the exact bug this exists to fix.
 *
 * `z-overlay` deliberately sits UNDER `z-popup`: a second incoming call, and
 * the message popups, must be able to appear over a call in progress.
 *
 * ON THE DESKTOP THE OS DOES THIS INSTEAD. A call there runs in a window of its
 * own, so minimising that window IS the corner tile and its taskbar entry is
 * the way back — better than a tile, because it survives the workspace being
 * hidden in the tray. Two consequences, both enforced here rather than left to
 * a route comparison a future deep link could break:
 *
 *   • in the desktop WORKSPACE window nothing may mount, or the call would
 *     render twice — once here and once in the window the shell opened;
 *   • in the desktop CALL window it is always full-bleed, because that window
 *     never goes anywhere else and `compact` there would be a tile floating in
 *     an otherwise empty window.
 */
export function ActiveCallHost() {
  const live = useStoreValue(liveCallStore, selectLiveCall);
  const location = useLocation();

  const mode = callSurfaceMode();
  /* The desktop workspace never calls `liveCallActions.enter`, so this is
     belt-and-braces: a stray `enter` must not be able to resurrect a second
     surface for a call that already has a window of its own. */
  if (live === null || mode === 'window') return null;

  const onCallRoute = isDesktopCallWindow() || location.pathname === ROUTES.call(live.callId);

  return (
    <div
      className={
        onCallRoute
          ? 'fixed inset-0 z-overlay bg-background'
          : 'pointer-events-none fixed bottom-5 left-5 z-overlay'
      }
    >
      <CallSurface callId={live.callId} compact={!onCallRoute} />
    </div>
  );
}
